import { AffiliateAccountStatus, AffiliateApplyResponse } from '@avanzu/types';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import PaperSection from '../../components/PaperSection';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../services/apiClient';

const BENEFITS = [
  {
    title: 'Earn commission',
    text: "Receive a share of your referred client's first paid project — tracked automatically from signup to payment.",
  },
  {
    title: 'Your clients save',
    text: 'Organisations that sign up with your referral link get a discount on their first paid project.',
  },
  {
    title: 'Nothing to manage',
    text: 'Share your link or code; attribution, discounts and commission accrual are handled by Avanzu.',
  },
];

const AffiliateProgramPage: React.FC = () => {
  const { affiliateStatus, refreshUserState } = useAuth();

  const [businessName, setBusinessName] = useState('');
  const [website, setWebsite] = useState('');
  const [phone, setPhone] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (affiliateStatus === AffiliateAccountStatus.APPROVED) {
    return <Navigate to="/app" replace />;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const response = await apiClient.post('/affiliate/apply', {
        businessName: businessName || undefined,
        website: website || undefined,
        phone: phone || undefined,
        acceptedTerms,
      });

      const data: AffiliateApplyResponse = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to submit application');
      }

      await refreshUserState();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (affiliateStatus === AffiliateAccountStatus.PENDING) {
    return (
      <PaperSection title="Affiliate program">
        <Alert severity="info">
          Your application is under review. We'll email you as soon as a decision has been made —
          your referral code and dashboard unlock on approval.
        </Alert>
      </PaperSection>
    );
  }

  if (affiliateStatus === AffiliateAccountStatus.SUSPENDED) {
    return (
      <PaperSection title="Affiliate program">
        <Alert severity="error">
          Your affiliate account has been suspended and your referral code is inactive. If you
          believe this is an error, please contact admin@avanzu.com.
        </Alert>
      </PaperSection>
    );
  }

  // No affiliate account yet — show benefits + application form
  return (
    <Stack spacing={3}>
      <PaperSection title="Join the Avanzu affiliate program">
        <Grid container spacing={3}>
          {BENEFITS.map((benefit) => (
            <Grid item xs={12} md={4} key={benefit.title}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                {benefit.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {benefit.text}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </PaperSection>

      <PaperSection title="Apply now">
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2} sx={{ maxWidth: 480 }}>
            <TextField
              label="Business name (optional)"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              size="small"
            />
            <TextField
              label="Website (optional)"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://"
              size="small"
            />
            <TextField
              label="Phone (optional)"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              size="small"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                />
              }
              label="I accept the affiliate program terms and conditions"
            />
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || !acceptedTerms}
              sx={{ alignSelf: 'flex-start' }}
            >
              {isSubmitting ? 'Submitting…' : 'Submit application'}
            </Button>
          </Stack>
        </Box>
      </PaperSection>
    </Stack>
  );
};

export default AffiliateProgramPage;
